// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token') || '');
  const [role, setRole]   = useState(() => localStorage.getItem('role')  || '');
  const [user, setUser]   = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isValidating, setIsValidating] = useState(false);
  const [hasValidated, setHasValidated] = useState(() => {
    // Check if we've already validated this session
    return localStorage.getItem('tokenValidated') === 'true';
  });

  // Validate token on app startup
  const validateToken = async () => {
    if (!token || hasValidated) return false;
    
    setIsValidating(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Token is valid, update user data if needed
      if (response.data.user && !user) {
        setUser(response.data.user);
      }
      
      setIsValidating(false);
      setHasValidated(true);
      localStorage.setItem('tokenValidated', 'true'); // Mark as validated in localStorage
      return true;
    } catch (error) {
      // Token is invalid or expired
      console.log('Token validation failed:', error.response?.status);
      logout();
      setIsValidating(false);
      return false;
    }
  };

  // Validate token only once on app startup - DISABLED for now to prevent navigation delays
  // useEffect(() => {
  //   if (token && !hasValidated) {
  //     validateToken();
  //   }
  // }, [token, hasValidated]); // Only run when token changes or hasn't been validated

  // Persist to localStorage
  useEffect(() => {
    if (token) localStorage.setItem('token', token);
    else      localStorage.removeItem('token');
  }, [token]);

  useEffect(() => {
    if (role) localStorage.setItem('role', role);
    else      localStorage.removeItem('role');
  }, [role]);

  useEffect(() => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else      localStorage.removeItem('user');
  }, [user]);

  const login = ({ token, role, user }) => {
    setToken(token);
    setRole(role);
    setUser(user);
    setHasValidated(false); // Reset validation flag on login
  };

  const logout = () => {
    setToken('');
    setRole('');
    setUser(null);
    setHasValidated(false); // Reset validation flag on logout
    localStorage.removeItem('tokenValidated'); // Clear validation flag
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ token, role, user, login, logout, updateUser, isValidating }}>
      {children}
    </AuthContext.Provider>
  );
}
